import { createContext, useContext, useReducer, useMemo, useEffect, useRef, useCallback } from 'react';
import { useStatsigUser, type StatsigUser } from '@statsig/react-bindings';
import { datadogRum } from '@datadog/browser-rum';

import { reducer, initialState } from 'modules/Auth/AuthProvider/reducer';
import { AuthContextType, AuthContextProviderProps } from 'modules/Auth/AuthProvider/Auth.interface';
import actions from 'modules/Auth/AuthProvider/actions';
import { buildAnonymousStatsigUser, isBotUser } from 'modules/common/integrations/Statsig/statsigUser';
import { getAnonymousId, onTrackingScriptReady } from 'modules/common/components/AnalyticsTracking/trackingScript';

const AuthContext = createContext<AuthContextType>({} as AuthContextType);
AuthContext.displayName = 'AuthContext';

export const useAuthContext = () => useContext(AuthContext);

export function AuthContextProvider({ children, ...props }: AuthContextProviderProps) {
    const [state, dispatch] = useReducer(reducer, { ...initialState, ...props });
    const value = useMemo<AuthContextType>(() => ({ state, actions, dispatch }), [state, dispatch]);
    const { updateUserAsync, user: statsigUser } = useStatsigUser();

    const { user } = state;

    // Serialize Statsig identity transitions. Logout and login both trigger an
    // update, and a rapid auth flip could otherwise let two fire-and-forget
    // calls resolve out of order and leave Statsig on the wrong user. Chaining
    // guarantees the last-requested identity is applied last; failures are
    // reported rather than surfacing as unhandled rejections.
    const statsigUpdateQueue = useRef<Promise<unknown>>(Promise.resolve());
    const queueStatsigUserUpdate = useCallback(
        (nextUser: StatsigUser | ((currentUser: StatsigUser) => StatsigUser)) => {
            statsigUpdateQueue.current = statsigUpdateQueue.current
                .then(() => updateUserAsync(nextUser))
                .catch((error) => {
                    datadogRum.addError(error instanceof Error ? error : new Error(String(error)), {
                        source: 'AuthProvider.updateStatsigUser',
                    });
                });
        },
        [updateUserAsync]
    );

    const statsigUserRef = useRef(statsigUser);

    useEffect(() => {
        statsigUserRef.current = statsigUser;
    }, [statsigUser]);

    // The Statsig user is built at module load, usually before the tracking script has loaded, so
    // its `segmentAnonymousID` can be a fallback rather than the script's id. Experiments bucket on
    // that id, so swap in the real one as soon as the script is ready.
    useEffect(() => {
        if (isBotUser()) {
            return;
        }

        onTrackingScriptReady(() => {
            const segmentAnonymousID = getAnonymousId();

            if (statsigUserRef.current?.customIDs?.segmentAnonymousID === segmentAnonymousID) {
                return;
            }

            // Applied to the user Statsig holds when the update runs, not this render's copy, so a
            // login queued ahead of it is not rolled back.
            queueStatsigUserUpdate((currentUser) => ({
                ...currentUser,
                customIDs: { ...currentUser.customIDs, segmentAnonymousID },
            }));
        });
    }, [queueStatsigUserUpdate]);

    useEffect(() => {
        if (!statsigUser) {
            return;
        }

        // Bot sessions (e2e runner, monitors) keep the masked 'bot' identity
        // through login/logout — syncing the real pk here would defeat the
        // Statsig bot-exclusion rules and re-fire on every render ('bot' != pk).
        if (isBotUser()) {
            return;
        }

        // Logged out: drop Statsig back to the anonymous identity so a stale
        // authenticated user no longer resolves gates, and the next login is a
        // real identity change that forces a fresh evaluation. Trigger on the
        // presence of an email (only authenticated users have one) rather than
        // rebuilding the anonymous user to compare ids — this avoids touching
        // the cookie/analytics utils on every logged-out render and converges
        // after a single reset.
        if (!user?.pk) {
            if (statsigUser.email) {
                queueStatsigUserUpdate(buildAnonymousStatsigUser());
            }
            return;
        }

        // Only update if the values are different
        if (statsigUser.userID !== user?.pk.toString() || statsigUser.email !== user?.email) {
            const userID = user.pk.toString();
            const email = user?.email;

            queueStatsigUserUpdate((currentUser) => ({ ...currentUser, userID, email }));
        }
    }, [statsigUser, user?.pk, user?.email, queueStatsigUserUpdate]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
